import { AlertTriangle, BarChart3, Clock3, Sparkles } from 'lucide-react'
import { useCurrentUser } from '@axilon-apps/domains-identity/state'

import { cn } from '../../lib/utils'
import { CHAT_LANDING_MEASURE } from './chat-measure'
import {
  firstNameFromDisplayName,
  greetingForHour,
  LANDING_TASK_CARDS,
  type LandingTaskCard
} from './chat-landing'

const KIND_ICON = {
  diagnose: Sparkles,
  alarms: AlertTriangle,
  history: Clock3,
  build: BarChart3
} as const

const KIND_TONE: Record<LandingTaskCard['id'], string> = {
  diagnose: 'text-primary',
  alarms: 'text-st-warn',
  history: 'text-sess',
  build: 'text-st-run'
}

export function ChatLandingHero() {
  const currentUser = useCurrentUser()
  const displayName =
    (typeof currentUser?.name === 'string' && currentUser.name) ||
    (typeof currentUser?.username === 'string' && currentUser.username) ||
    undefined
  const firstName = firstNameFromDisplayName(displayName)
  const greeting = greetingForHour(new Date().getHours(), firstName)

  return (
    <div data-testid="chat-landing" className={cn(CHAT_LANDING_MEASURE, 'text-center')}>
      <div className="mx-auto mb-4 grid size-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
        <Sparkles className="size-5 fill-current" />
      </div>
      <h1 className="m-0 text-25 font-semibold tracking-tight text-foreground">{greeting}</h1>
      <p className="mt-1.5 mb-0 text-15 text-muted-foreground">What do you want to know?</p>
    </div>
  )
}

export function ChatLandingCards({
  onPickPrompt,
  cards = LANDING_TASK_CARDS
}: {
  onPickPrompt: (card: LandingTaskCard) => void
  cards?: LandingTaskCard[]
}) {
  return (
    <div className={cn(CHAT_LANDING_MEASURE, 'grid grid-cols-1 gap-2.5 sm:grid-cols-2')}>
      {cards.map((card) => {
        const Icon = KIND_ICON[card.id]
        return (
          <button
            key={card.id}
            type="button"
            onClick={() => onPickPrompt(card)}
            className="rounded-lg border border-border bg-card px-3.5 py-3 text-left transition-colors hover:border-primary/40 hover:shadow-sm"
          >
            <span
              className={cn(
                'mb-1.5 flex items-center gap-1.5 text-10 font-bold tracking-[0.1em] uppercase',
                KIND_TONE[card.id]
              )}
            >
              <Icon className="size-3" />
              {card.label}
            </span>
            <span className="block text-15 font-medium text-foreground">{card.prompt}</span>
          </button>
        )
      })}
    </div>
  )
}

export function ChatLanding({ onPickPrompt }: { onPickPrompt: (card: LandingTaskCard) => void }) {
  return (
    <div className="flex flex-col gap-6 duration-300 animate-in fade-in-0 motion-reduce:animate-none">
      <ChatLandingHero />
      <ChatLandingCards onPickPrompt={onPickPrompt} />
    </div>
  )
}
